import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
} from "firebase/firestore";

/* ─── THEME TOKENS (same style as ChatApp) ─── */
const THEMES = {
  light: {
    bg: "#ffffff",
    bgSub: "#f7f7f8",
    surface: "#f0f0f2",
    surfaceHover: "#e8e8eb",
    border: "#e3e3e6",
    borderStrong: "#c5c5ca",
    text: "#111111",
    textSub: "#5f5f6b",
    textMuted: "#a3a3b0",
    accent: "#1a1a1a",
    accentText: "#ffffff",
    danger: "#d93025",
    success: "#166534",
    successBg: "#ecfdf5",
    inputBg: "#f7f7f8",
    inputBorder: "#e3e3e6",
    sidebarBg: "#f7f7f8",
    sidebarBorder: "#e3e3e6",
    shadow: "0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.05)",
    shadowMd: "0 4px 20px rgba(0,0,0,0.08)",
  },
  dark: {
    bg: "#212121",
    bgSub: "#2a2a2a",
    surface: "#303030",
    surfaceHover: "#3a3a3a",
    border: "#3a3a3a",
    borderStrong: "#4e4e4e",
    text: "#ececec",
    textSub: "#9e9e9e",
    textMuted: "#616161",
    accent: "#ececec",
    accentText: "#111111",
    danger: "#f28b82",
    success: "#86efac",
    successBg: "#052e16",
    inputBg: "#2a2a2a",
    inputBorder: "#3a3a3a",
    sidebarBg: "#1a1a1a",
    sidebarBorder: "#2e2e2e",
    shadow: "0 1px 3px rgba(0,0,0,0.3)",
    shadowMd: "0 4px 20px rgba(0,0,0,0.35)",
  },
};

const buildCSS = (t) => `
  *, *::before, *::after { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; font-family: 'Google Sans Text', 'Google Sans', sans-serif; }
  ::-webkit-scrollbar { width: 6px; height: 6px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: ${t.border}; border-radius: 999px; }

  @keyframes fadeUp { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
  @keyframes fadeIn { from{opacity:0} to{opacity:1} }
  @keyframes spin { to{transform:rotate(360deg)} }

  .fade-up { animation: fadeUp .25s ease forwards; }
  .fade-in { animation: fadeIn .2s ease; }

  .icon-btn {
    display: flex; align-items: center; justify-content: center;
    width: 38px; height: 38px; border-radius: 10px; border: 1px solid ${t.border};
    background: ${t.bg}; color: ${t.textSub};
    cursor: pointer; transition: .15s ease;
  }
  .icon-btn:hover { background: ${t.surface}; color: ${t.text}; }

  .primary-btn {
    border: none;
    background: ${t.accent};
    color: ${t.accentText};
    padding: 12px 16px;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: .15s ease;
    box-shadow: ${t.shadow};
  }
  .primary-btn:hover:not(:disabled) { opacity: .85; transform: translateY(-1px); }
  .primary-btn:disabled { opacity: .5; cursor: not-allowed; }

  .secondary-btn {
    border: 1px solid ${t.border};
    background: ${t.bg};
    color: ${t.text};
    padding: 12px 16px;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: .15s ease;
  }
  .secondary-btn:hover:not(:disabled) { background: ${t.surface}; }
  .secondary-btn:disabled { opacity: .5; cursor: not-allowed; }

  .danger-btn {
    border: 1px solid ${t.border};
    background: transparent;
    color: ${t.danger};
    padding: 12px 16px;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: .15s ease;
  }
  .danger-btn:hover:not(:disabled) { background: ${t.danger}14; }

  .card {
    background: ${t.bg};
    border: 1px solid ${t.border};
    border-radius: 18px;
    box-shadow: ${t.shadow};
  }

  .material-icons-round { font-size: 20px; line-height: 1; user-select: none; }
`;

function formatDate(ts) {
  if (!ts) return "Unknown date";
  try {
    if (typeof ts?.toDate === "function") return ts.toDate().toLocaleString();
    if (ts?.seconds) return new Date(ts.seconds * 1000).toLocaleString();
    return new Date(ts).toLocaleString();
  } catch {
    return "Unknown date";
  }
}

export default function Download() {
  const [dark, setDark] = useState(() => localStorage.getItem("darkMode") === "true");
  const [mobile, setMobile] = useState(window.innerWidth <= 768);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [copying, setCopying] = useState(false);
  const [chats, setChats] = useState([]);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const T = dark ? THEMES.dark : THEMES.light;

  const email = localStorage.getItem("email");
  const userName = localStorage.getItem("name");
  const userId = localStorage.getItem("userId");
  const loggedIn = !!(email && userName && userId);

  useEffect(() => {
    const styleId = "omrali-download-theme";
    let styleTag = document.getElementById(styleId);

    if (!styleTag) {
      styleTag = document.createElement("style");
      styleTag.id = styleId;
      document.head.appendChild(styleTag);
    }

    styleTag.innerHTML = buildCSS(T);
  }, [dark, T]);

  useEffect(() => {
    localStorage.setItem("darkMode", dark);
  }, [dark]);

  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const loadChats = async () => {
      if (!loggedIn) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");

      try {
        const q = query(
          collection(db, "conversations"),
          where("userId", "==", userId),
          orderBy("timestamp", "desc")
        );

        const snap = await getDocs(q);

        const rows = snap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setChats(rows);
      } catch (err) {
        console.error("Error loading conversations:", err);
        setError(
          "Could not load chats."
        );
      } finally {
        setLoading(false);
      }
    };

    loadChats();
  }, [loggedIn, userId]);

  const stats = useMemo(() => {
    const totalMessages = chats.length;
    const totalWords = chats.reduce((acc, item) => {
      const userWords = (item.message || "").trim().split(/\s+/).filter(Boolean).length;
      const aiWords = (item.response || "").trim().split(/\s+/).filter(Boolean).length;
      return acc + userWords + aiWords;
    }, 0);

    return {
      totalMessages,
      totalWords,
    };
  }, [chats]);

  const buildTxtContent = () => {
    const lines = [];

    lines.push("OMRALI AI — CHAT EXPORT");
    lines.push(`User: ${userName || "Unknown User"}`);
    lines.push(`Email: ${email || "Unknown Email"}`);
    lines.push(`Exported At: ${new Date().toLocaleString()}`);
    lines.push(`Total Conversations: ${chats.length}`);
    lines.push("=".repeat(60));
    lines.push("");

    chats
      .slice()
      .reverse()
      .forEach((item, idx) => {
        lines.push(`Conversation #${idx + 1}`);
        lines.push(`Date: ${formatDate(item.timestamp)}`);
        lines.push(`Model: ${item.model || "Unknown"}`);
        lines.push("");
        lines.push(`You:`);
        lines.push(item.message || "");
        lines.push("");
        lines.push(`Omrali AI:`);
        lines.push(item.response || "");
        lines.push("");
        lines.push("-".repeat(60));
        lines.push("");
      });

    return lines.join("\n");
  };

  const buildJsonContent = () => {
    return JSON.stringify(
      {
        exportedAt: new Date().toISOString(),
        user: {
          name: userName || null,
          email: email || null,
          userId: userId || null,
        },
        totalConversations: chats.length,
        conversations: chats
          .slice()
          .reverse()
          .map((item, idx) => ({
            index: idx + 1,
            id: item.id,
            timestamp: formatDate(item.timestamp),
            model: item.model || null,
            message: item.message || "",
            response: item.response || "",
            isGuest: !!item.isGuest,
          })),
      },
      null,
      2
    );
  };

  const downloadFile = (content, fileName, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();

    URL.revokeObjectURL(url);
  };

  const handleDownloadTxt = async () => {
    if (!chats.length) return;
    setDownloading(true);
    try {
      const content = buildTxtContent();
      downloadFile(
        content,
        `omrali-chats-${(userName || "user").replace(/\s+/g, "-").toLowerCase()}.txt`,
        "text/plain;charset=utf-8"
      );
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadJson = async () => {
    if (!chats.length) return;
    setDownloading(true);
    try {
      const content = buildJsonContent();
      downloadFile(
        content,
        `omrali-chats-${(userName || "user").replace(/\s+/g, "-").toLowerCase()}.json`,
        "application/json;charset=utf-8"
      );
    } finally {
      setDownloading(false);
    }
  };

  const handleCopyAll = async () => {
    if (!chats.length) return;
    setCopying(true);
    setCopied(false);

    try {
      await navigator.clipboard.writeText(buildTxtContent());
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      alert("Copy failed. Your browser may have blocked clipboard access.");
    } finally {
      setCopying(false);
    }
  };

  const handleClearLocalOnly = () => {
    const ok = window.confirm(
      "This only clears your local browser login/session data. It will NOT delete saved chats from your account. Continue?"
    );
    if (!ok) return;

    const darkMode = localStorage.getItem("darkMode");
    localStorage.clear();
    if (darkMode !== null) localStorage.setItem("darkMode", darkMode);

    window.location.href = "/";
  };

  const Icon = ({ n, style }) => (
    <span className="material-icons-round" style={style}>
      {n}
    </span>
  );

  return (
    <div
      style={{
        minHeight: "100vh",
        background: T.bg,
        color: T.text,
        fontFamily: "'Google Sans Text', 'Google Sans', sans-serif",
      }}
    >
      {/* Header */}
      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 20,
          background: T.bg,
          borderBottom: `1px solid ${T.border}`,
          padding: mobile ? "12px 14px" : "14px 22px",
        }}
      >
        <div
          style={{
            maxWidth: 1100,
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button
              className="icon-btn"
              onClick={() => window.history.back()}
              title="Go back"
            >
              <Icon n="arrow_back" />
            </button>

            <div>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: mobile ? 16 : 17,
                  letterSpacing: "-0.2px",
                }}
              >
                Omrali <span style={{ color: T.textSub, fontWeight: 500 }}>AI</span>
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: T.textMuted,
                  marginTop: 2,
                }}
              >
                Download your chat history
              </div>
            </div>
          </div>

          <button
            className="icon-btn"
            onClick={() => setDark((d) => !d)}
            title="Toggle theme"
          >
            <Icon n={dark ? "light_mode" : "dark_mode"} />
          </button>
        </div>
      </header>

      {/* Main */}
      <main
        style={{
          maxWidth: 1100,
          margin: "0 auto",
          padding: mobile ? "18px 14px 28px" : "28px 22px 40px",
        }}
      >
        {/* Hero */}
        <section
          className="fade-in"
          style={{
            marginBottom: 22,
            padding: mobile ? "18px" : "22px",
            border: `1px solid ${T.border}`,
            borderRadius: 22,
            background: T.bgSub,
            boxShadow: T.shadow,
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 10px",
              borderRadius: 999,
              background: T.surface,
              border: `1px solid ${T.border}`,
              fontSize: 12,
              fontWeight: 600,
              color: T.textSub,
              marginBottom: 14,
            }}
          >
            <Icon n="download" style={{ fontSize: 16 }} />
            Export Center
          </div>

          <h1
            style={{
              fontSize: mobile ? 26 : 36,
              lineHeight: 1.15,
              letterSpacing: "-0.8px",
              fontWeight: 800,
              margin: 0,
            }}
          >
            Download your chats
          </h1>

          <p
            style={{
              marginTop: 10,
              color: T.textSub,
              fontSize: 14.5,
              lineHeight: 1.7,
              maxWidth: 720,
            }}
          >
            Export all your saved Omrali AI conversations in clean text or JSON
            format. This page only shows chats stored for your signed-in account.
          </p>
        </section>

        {!loggedIn ? (
          <div
            className="fade-up card"
            style={{
              padding: mobile ? 20 : 28,
              textAlign: "center",
            }}
          >
            <div
              style={{
                width: 56,
                height: 56,
                margin: "0 auto 14px",
                borderRadius: 16,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: T.surface,
                border: `1px solid ${T.border}`,
              }}
            >
              <Icon n="lock" style={{ fontSize: 24, color: T.textSub }} />
            </div>

            <h2 style={{ fontSize: 22, margin: 0, fontWeight: 700 }}>
              Sign in required
            </h2>

            <p
              style={{
                marginTop: 10,
                color: T.textSub,
                fontSize: 14.5,
                lineHeight: 1.7,
                maxWidth: 520,
                marginInline: "auto",
              }}
            >
              You’re currently in guest mode. Guest chats are not stored, so there’s nothing to export. Sign in to save and
              download future conversations.
            </p>

            <div
              style={{
                marginTop: 18,
                display: "flex",
                justifyContent: "center",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <button
                className="primary-btn"
                onClick={() => (window.location.href = "/login")}
              >
                Sign In
              </button>
              <button
                className="secondary-btn"
                onClick={() => (window.location.href = "/")}
              >
                Go Home
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Stats */}
            <section
              className="fade-up"
              style={{
                display: "grid",
                gridTemplateColumns: mobile ? "1fr" : "repeat(3, 1fr)",
                gap: 14,
                marginBottom: 18,
              }}
            >
              {[
                {
                  label: "Signed in as",
                  value: userName || "User",
                  sub: email || "",
                  icon: "person",
                },
                {
                  label: "Saved conversations",
                  value: String(stats.totalMessages),
                  sub: "Stored in Account",
                  icon: "forum",
                },
                {
                  label: "Approx. total words",
                  value: String(stats.totalWords),
                  sub: "User + AI combined",
                  icon: "notes",
                },
              ].map((item) => (
                <div
                  key={item.label}
                  className="card"
                  style={{
                    padding: 18,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 12,
                  }}
                >
                  <div
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: 12,
                      background: T.surface,
                      border: `1px solid ${T.border}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Icon n={item.icon} style={{ fontSize: 20, color: T.textSub }} />
                  </div>

                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 12,
                        color: T.textMuted,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: ".04em",
                      }}
                    >
                      {item.label}
                    </div>
                    <div
                      style={{
                        marginTop: 4,
                        fontSize: 18,
                        fontWeight: 700,
                        color: T.text,
                        wordBreak: "break-word",
                      }}
                    >
                      {item.value}
                    </div>
                    <div
                      style={{
                        marginTop: 4,
                        fontSize: 13,
                        color: T.textSub,
                        wordBreak: "break-word",
                      }}
                    >
                      {item.sub}
                    </div>
                  </div>
                </div>
              ))}
            </section>

            {/* Error */}
            {error && (
              <div
                className="fade-up"
                style={{
                  marginBottom: 16,
                  padding: "14px 16px",
                  borderRadius: 14,
                  border: `1px solid ${T.border}`,
                  background: dark ? "#2b1f1f" : "#fef2f2",
                  color: T.danger,
                  fontSize: 14,
                  lineHeight: 1.6,
                }}
              >
                {error}
              </div>
            )}

            {/* Actions */}
            <section
              className="fade-up card"
              style={{
                padding: mobile ? 18 : 22,
                marginBottom: 18,
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: mobile ? "column" : "row",
                  alignItems: mobile ? "stretch" : "center",
                  justifyContent: "space-between",
                  gap: 14,
                }}
              >
                <div>
                  <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>
                    Export options
                  </h2>
                  <p
                    style={{
                      marginTop: 8,
                      color: T.textSub,
                      fontSize: 14,
                      lineHeight: 1.7,
                    }}
                  >
                    Choose a format to download your entire saved conversation history.
                  </p>
                </div>

                {copied && (
                  <div
                    style={{
                      padding: "8px 12px",
                      borderRadius: 999,
                      background: T.successBg,
                      color: T.success,
                      fontSize: 12.5,
                      fontWeight: 700,
                      border: `1px solid ${dark ? "#14532d" : "#a7f3d0"}`,
                    }}
                  >
                    Copied to clipboard
                  </div>
                )}
              </div>

              <div
                style={{
                  marginTop: 16,
                  display: "grid",
                  gridTemplateColumns: mobile ? "1fr" : "repeat(4, minmax(0, 1fr))",
                  gap: 10,
                }}
              >
                <button
                  className="primary-btn"
                  onClick={handleDownloadTxt}
                  disabled={!chats.length || downloading || loading}
                >
                  {downloading ? "Preparing..." : "Download TXT"}
                </button>

                <button
                  className="secondary-btn"
                  onClick={handleDownloadJson}
                  disabled={!chats.length || downloading || loading}
                >
                  Download JSON
                </button>

                <button
                  className="secondary-btn"
                  onClick={handleCopyAll}
                  disabled={!chats.length || copying || loading}
                >
                  {copying ? "Copying..." : "Copy All"}
                </button>

                <button
                  className="danger-btn"
                  onClick={handleClearLocalOnly}
                >
                  Clear Local Session
                </button>
              </div>
            </section>

            {/* Chat Preview */}
            <section className="fade-up card" style={{ overflow: "hidden" }}>
              <div
                style={{
                  padding: mobile ? "16px 16px 14px" : "18px 20px 16px",
                  borderBottom: `1px solid ${T.border}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <div>
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
                    Saved chat preview
                  </h3>
                  <p
                    style={{
                      marginTop: 6,
                      fontSize: 13.5,
                      color: T.textSub,
                    }}
                  >
                    Showing your latest stored conversations from your account.
                  </p>
                </div>

                <div
                  style={{
                    fontSize: 12,
                    color: T.textMuted,
                    fontWeight: 600,
                    background: T.surface,
                    border: `1px solid ${T.border}`,
                    padding: "6px 10px",
                    borderRadius: 999,
                  }}
                >
                  {loading ? "Loading..." : `${chats.length} items`}
                </div>
              </div>

              <div
                style={{
                  maxHeight: 560,
                  overflowY: "auto",
                  padding: mobile ? 14 : 18,
                  background: T.bg,
                }}
              >
                {loading ? (
                  <div
                    style={{
                      padding: 24,
                      textAlign: "center",
                      color: T.textSub,
                      fontSize: 14,
                    }}
                  >
                    Loading saved chats...
                  </div>
                ) : chats.length === 0 ? (
                  <div
                    style={{
                      padding: 24,
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{
                        width: 52,
                        height: 52,
                        borderRadius: 14,
                        margin: "0 auto 14px",
                        background: T.surface,
                        border: `1px solid ${T.border}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Icon n="inbox" style={{ fontSize: 24, color: T.textSub }} />
                    </div>

                    <h4 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
                      No saved chats yet
                    </h4>

                    <p
                      style={{
                        marginTop: 8,
                        color: T.textSub,
                        fontSize: 14,
                        lineHeight: 1.7,
                        maxWidth: 520,
                        marginInline: "auto",
                      }}
                    >
                      Start chatting while signed in, and your conversations will
                      appear here automatically for export.
                    </p>

                    <button
                      className="primary-btn"
                      style={{ marginTop: 14 }}
                      onClick={() => (window.location.href = "/")}
                    >
                      Start Chatting
                    </button>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    {chats.map((item, idx) => (
                      <div
                        key={item.id || idx}
                        style={{
                          border: `1px solid ${T.border}`,
                          borderRadius: 16,
                          overflow: "hidden",
                          background: T.bgSub,
                        }}
                      >
                        <div
                          style={{
                            padding: "12px 14px",
                            borderBottom: `1px solid ${T.border}`,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 12,
                            flexWrap: "wrap",
                          }}
                        >
                          <div
                            style={{
                              fontSize: 13,
                              color: T.textSub,
                              fontWeight: 600,
                            }}
                          >
                            {formatDate(item.timestamp)}
                          </div>

                          <div
                            style={{
                              fontSize: 12,
                              color: T.textMuted,
                              background: T.surface,
                              border: `1px solid ${T.border}`,
                              borderRadius: 999,
                              padding: "5px 9px",
                              fontWeight: 600,
                            }}
                          >
                            {item.model || "Unknown Model"}
                          </div>
                        </div>

                        <div style={{ padding: 14 }}>
                          <div
                            style={{
                              marginBottom: 12,
                              padding: 12,
                              borderRadius: 12,
                              background: T.accent,
                              color: T.accentText,
                              fontSize: 14,
                              lineHeight: 1.65,
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                            }}
                          >
                            <div
                              style={{
                                fontSize: 11,
                                fontWeight: 700,
                                opacity: 0.8,
                                marginBottom: 6,
                                textTransform: "uppercase",
                                letterSpacing: ".04em",
                              }}
                            >
                              You
                            </div>
                            {item.message || "—"}
                          </div>

                          <div
                            style={{
                              padding: 12,
                              borderRadius: 12,
                              background: T.bg,
                              border: `1px solid ${T.border}`,
                              color: T.text,
                              fontSize: 14,
                              lineHeight: 1.7,
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                            }}
                          >
                            <div
                              style={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: T.textMuted,
                                marginBottom: 6,
                                textTransform: "uppercase",
                                letterSpacing: ".04em",
                              }}
                            >
                              Omrali AI
                            </div>
                            {item.response || "—"}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}